import { useDispatch, useSelector } from 'react-redux';
import { getScopeRole } from '@/services/current-context';
import type { RootState } from '../../../store/store';
import { useCaptionSortPhotosetMutation } from '../services/assetApi';
import { applyPhotosetOrder } from '../slice/ImageAssetSlice';
import { showAlert } from '@/slice/AlertSlice';
import { Variants } from '@/types/common';
import { canSync } from '@/util/roles';

export const CAPTION_SORT_MESSAGES = {
  sorted: 'Sorted into your preferred sequence. Sync to update.',
  // DV-3519: for a role without Sync — don't point at a button
  // they do not have.
  sortedNoSync: 'Sorted into your preferred sequence.',
  // The server sorted, but the response carried no order to apply (a backend
  // that predates photoset.assetUUIDs). The write happened; only the grid is
  // behind — say so rather than claim failure or fake success.
  orderUnavailable:
    'Photos were sorted, but the page could not update — refresh to see the new order.',
  failed: 'Could not sort photos by caption. Please try again.'
} as const;

/**
 * DV-2859 Caption Sort: one click, one POST, then the grid adopts the order
 * the server returned. No undo — a drag afterwards is the undo, same as
 * today. The only confirmation is DV-3537's missing-caption warning, which
 * lives in front of this hook (useCaptionSortPrompt), not in it.
 *
 * The sort itself is server-side (DV-2723 BE handoff): it matches captions
 * to the Default Caption Sequence, stable-sorts, and writes through the same
 * reorder path a drag uses, so sync arming and Legacy propagation are
 * identical. What is left for the UI is bookkeeping:
 *
 *  - Re-sequence the slice from `photoset.assetUUIDs` via applyPhotosetOrder,
 *    which flags the moved photos orderDirty so the unsynced badge lights —
 *    the manual reorder path never refetches the photoset, and neither does
 *    this (see AssetsImageGrid's drop handler). applyPhotosetOrder itself
 *    no-ops when the order is already identical, so this dispatches
 *    unconditionally rather than duplicating that check here.
 *  - Tell the user the sort completed, regardless of whether anything moved
 *    (a photoset already in sequence is not an error case).
 *  - Swallow a re-click while a sort is in flight. The server would no-op the
 *    second call anyway; this just avoids a second toast.
 *
 * Which photoset: the first grid item that carries one, exactly as the drag
 * path resolves it (position 0 can legitimately lack a photosetUUID — a
 * just-uploaded asset — while the rest of the list is tied to a real set).
 */
export const selectGridPhotosetId = (state: RootState): string | undefined =>
  state.imageAsset.imageAssets.find((image) => image.photosetUUID)
    ?.photosetUUID;

export function useCaptionSort() {
  const dispatch = useDispatch();
  const photosetId = useSelector(selectGridPhotosetId);
  const [captionSort, { isLoading: sorting }] =
    useCaptionSortPhotosetMutation();

  // DV-3537: `targetId` pins the photoset a confirmation was asked about, so
  // a set switched under an open dialog is never the one sorted.
  const runCaptionSort = async (
    targetId: string | undefined = photosetId
  ): Promise<void> => {
    if (!targetId || sorting) return;
    try {
      const photoset = await captionSort(targetId).unwrap();
      const serverOrder = photoset.assetUUIDs;
      if (!serverOrder) {
        dispatch(
          showAlert({
            type: Variants.WARNING,
            message: CAPTION_SORT_MESSAGES.orderUnavailable
          })
        );
        return;
      }
      dispatch(applyPhotosetOrder(serverOrder));
      dispatch(
        showAlert({
          type: Variants.SUCCESS,
          message: canSync(getScopeRole())
            ? CAPTION_SORT_MESSAGES.sorted
            : CAPTION_SORT_MESSAGES.sortedNoSync
        })
      );
    } catch {
      // Same treatment as a failed manual drag: one error toast, nothing to
      // roll back locally because nothing local changed before the response.
      dispatch(
        showAlert({
          type: Variants.ERROR,
          message: CAPTION_SORT_MESSAGES.failed
        })
      );
    }
  };

  return { runCaptionSort, sorting, canCaptionSort: Boolean(photosetId) };
}
